// PAQUETES
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { eventoLoginAttempt, eventoLoginSuccess, eventoLoginFailed } from "../funcionesDatos/eventoPropio";

// Carga el script de Google Identity Services una sola vez para toda la app.
let promesaScriptGoogle = null;
function cargarScriptGoogleIdentity() {
    if (window.google?.accounts?.id) return Promise.resolve();
    if (promesaScriptGoogle) return promesaScriptGoogle;

    promesaScriptGoogle = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("No se pudo cargar el script de Google Identity Services."));
        document.head.appendChild(script);
    });

    return promesaScriptGoogle;
}

// Páginas donde no tiene sentido mostrar el One Tap: ya está en un flujo de sesión propio,
// o es la zona de administración (staff, no clientes).
const RUTAS_EXCLUIDAS = ["/iniciar-sesion", "/registrar", "/referido", "/eliminar-datos", "/error-inicio-google", "/admin", "/administrador", "/administracion"];

// Google One Tap en CUALQUIER página pública: si alguien llega directo desde un anuncio a un
// servicio (sin pasar por Home ni por "Iniciar sesión") y ya tiene sesión de Google abierta en
// el navegador, le aparece la tarjetita nativa de Google ("Continuar como Fulano") sin moverse
// de la página. Se intenta UNA sola vez por carga de la app (no se repite en cada navegación).
// No hay botón propio de respaldo: la tarjeta con foto/nombre solo la puede mostrar Google (es
// su UI nativa, no se puede fijar como un widget siempre visible), así que si no aparece,
// simplemente no aparece — no hay una versión "fija" de esa misma tarjeta.
function GoogleOneTapGlobal({ usuario, cargandoUsuario, setUsuario }) {
    const location = useLocation();
    const yaIntentadoRef = useRef(false);

    useEffect(() => {
        if (cargandoUsuario) return; // esperamos a saber si ya hay sesión
        if (usuario) return; // ya está logueado, no hay nada que hacer
        if (yaIntentadoRef.current) return; // solo un intento por carga de la app

        const excluida = RUTAS_EXCLUIDAS.some((ruta) => location.pathname.startsWith(ruta));
        if (excluida) return;

        yaIntentadoRef.current = true;

        cargarScriptGoogleIdentity()
            .then(() => {
                window.google.accounts.id.initialize({
                    client_id: process.env.REACT_APP_ID_CLIENTE_GOOGLE,
                    auto_select: false,
                    cancel_on_tap_outside: true,
                    callback: async (respuestaGoogle) => {
                        try {
                            eventoLoginAttempt({ metodo: "google" });
                            const res = await axios.post("/auth/google-web", { idToken: respuestaGoogle.credential });
                            if (!res?.data?.autenticado) {
                                eventoLoginFailed({ metodo: "google", razon: "no_autenticado" });
                                return;
                            }

                            let usuarioLogueado = res.data.usuario;

                            // Si quedó en modo Vendedor, forzamos Comprador: si no, en cuanto
                            // actualicemos el usuario global, RutaZonaGlobalComprador lo saca de la
                            // página en la que está (ej. viendo un servicio desde un anuncio) para
                            // mandarlo a /vendedor — justo lo que NO queremos acá, la idea es que se
                            // quede exactamente donde estaba.
                            if (usuarioLogueado.modo === "Vendedor") {
                                try {
                                    const resModo = await axios.post("/cambiar-modo", {
                                        params: { usuario: usuarioLogueado.username, modoActual: "Vendedor" }
                                    });
                                    usuarioLogueado = { ...usuarioLogueado, modo: resModo?.data?.nuevoModo || "Comprador" };
                                } catch (err) {
                                    usuarioLogueado = { ...usuarioLogueado, modo: "Comprador" };
                                }
                            }

                            if (typeof setUsuario === "function") setUsuario(usuarioLogueado);

                            eventoLoginSuccess({ metodo: "google", usuarioId: usuarioLogueado?.idUsuario });

                            // Recargamos la página donde está (misma URL: window.location.reload
                            // no navega a ningún lado) para que todo lo que ya se renderizó con
                            // usuario=null se vuelva a construir con la sesión ya activa —
                            // el cambio de estado solo no bastaba en algunas pantallas.
                            window.location.reload();
                        } catch (err) {
                            eventoLoginFailed({ metodo: "google", razon: "error_red_o_servidor" });
                            console.log("Error con Google One Tap:", err);
                        }
                    },
                });

                window.google.accounts.id.prompt();
            })
            .catch((err) => console.log("Error cargando Google Identity Services (One Tap):", err));
    }, [cargandoUsuario, usuario, location.pathname, setUsuario]);

    return null;
}

export default GoogleOneTapGlobal;
